import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import MainLayout from "../components/MainLayout";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Button } from "../components/ui/button";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const HistoryPage = () => {
  const { getAuthHeaders, API, user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    fetchDayData(selectedDate);
  }, [selectedDate]);

  const fetchDayData = async (date) => {
    setLoading(true);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.get(`${API}/meals/date/${dateStr}`, {
        headers: getAuthHeaders()
      });
      setDayData(response.data);
    } catch (error) {
      console.error("Error fetching day data:", error);
      setDayData(null);
    } finally {
      setLoading(false);
    }
  };

  const navigateDay = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    if (newDate <= new Date()) {
      setSelectedDate(newDate);
    }
  };

  const isToday = format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
  const calorieGoal = dayData?.goals?.calories || user?.calorie_goal || 2000;
  const caloriesConsumed = dayData?.totals?.calories || 0;
  const progressPercentage = Math.min((caloriesConsumed / calorieGoal) * 100, 100);

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="history-page">
        {/* Date Navigation */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-semibold">Historial</h1>
            <p className="text-muted-foreground">Revisa tus comidas anteriores</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigateDay(-1)}
              data-testid="prev-day-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="min-w-[200px] justify-start text-left font-normal"
                  data-testid="date-picker-btn"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(selectedDate, "PPP", { locale: es })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    if (date) {
                      setSelectedDate(date);
                      setCalendarOpen(false);
                    }
                  }}
                  disabled={(date) => date > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigateDay(1)}
              disabled={isToday}
              data-testid="next-day-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-muted-foreground">Cargando...</div>
          </div>
        ) : (
          <>
            {/* Day Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-card border border-border/50 rounded-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold">Resumen del día</h3>
                {isToday && (
                  <span className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                    Hoy
                  </span>
                )}
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Calorías</span>
                    <span className="font-mono font-semibold" data-testid="history-calories">
                      {Math.round(caloriesConsumed)} / {calorieGoal} kcal
                    </span>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercentage}%` }}
                      transition={{ duration: 0.5 }}
                      className={`h-full rounded-full ${
                        progressPercentage > 100 ? "bg-destructive" : "bg-primary"
                      }`}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border/50">
                  <div className="text-center">
                    <span className="block font-mono text-xl font-semibold text-chart-protein">
                      {Math.round(dayData?.totals?.protein || 0)}g
                    </span>
                    <span className="text-xs text-muted-foreground">Proteína</span>
                  </div>
                  <div className="text-center">
                    <span className="block font-mono text-xl font-semibold text-chart-carbs">
                      {Math.round(dayData?.totals?.carbs || 0)}g
                    </span>
                    <span className="text-xs text-muted-foreground">Carbohidratos</span>
                  </div>
                  <div className="text-center">
                    <span className="block font-mono text-xl font-semibold text-chart-fat">
                      {Math.round(dayData?.totals?.fat || 0)}g
                    </span>
                    <span className="text-xs text-muted-foreground">Grasas</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Meals List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-card border border-border/50 rounded-2xl p-6"
            >
              <h3 className="font-heading font-semibold mb-4">Comidas registradas</h3>
              
              {dayData?.meals && dayData.meals.length > 0 ? (
                <div className="space-y-4">
                  {dayData.meals.map((meal, index) => (
                    <motion.div
                      key={meal.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 bg-muted/50 rounded-xl"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium">{meal.description}</p>
                          <p className="text-sm text-muted-foreground">{meal.time}</p>
                        </div>
                        <span className="font-mono font-semibold text-primary">
                          {Math.round(meal.total_calories)} kcal
                        </span>
                      </div>
                      
                      {meal.foods && meal.foods.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-border/50">
                          <div className="flex flex-wrap gap-2">
                            {meal.foods.map((food, i) => (
                              <span 
                                key={i}
                                className="px-2 py-1 bg-background rounded-md text-xs"
                              >
                                {food.name}: {Math.round(food.calories)} kcal
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-2 text-xs text-muted-foreground">
                        P: {Math.round(meal.total_protein)}g | C: {Math.round(meal.total_carbs)}g | G: {Math.round(meal.total_fat)}g
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No hay comidas registradas este día</p>
                </div>
              )}
            </motion.div>
          </>
        )}
      </div>
    </MainLayout>
  );
};

export default HistoryPage;
